<template>
<div>
  <div class="d-inline-flex align-items-center">
    <input @change="selected_file" type="file"
      class="form-control-file" style="width: auto;"
      data-testid="upload-file-input"
      :id="prefix">
    <button @click="upload(prefix)" type="button" class="btn btn-primary btn-sm ml-2 text-nowrap" data-testid="upload-file-start">{{ __('Upload') }}</button>
    <span class="ml-2 font-weight-bold text-nowrap" v-show="!uploading && (complete || cur_filename)">{{ __('Upload complete.') }}</span>
    <span class="ml-2 font-weight-bold text-nowrap" v-show="uploading">{{ __('Uploading.') }} {{ percents }}%</span>
  </div>
  <input type="hidden" :id="prefix + '_filename'" :name="prefix + '_filename'" :value="cur_filename" />
  <input type="hidden" :id="prefix + '_filesize'" :name="prefix + '_filesize'" :value="cur_filesize" />
  <input type="hidden" :id="prefix + '_original_name'" :name="prefix + '_original_name'" :value="cur_original_name" />
  <input type="hidden" :id="prefix + '_extension'" :name="prefix + '_extension'" :value="cur_extension" />
  <div v-if="preview_content" class="mt-2">
    <button type="button" class="btn btn-outline-info btn-sm mb-1" @click="show_preview = !show_preview">
      Preview <i :class="show_preview ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
    </button>
    <div v-show="show_preview" class="card card-body bg-light p-2" style="max-height: 200px; overflow-y: auto;">
      <pre class="mb-0" style="white-space: pre-wrap; font-size: 0.8em;">{{ preview_content }}</pre>
    </div>
  </div>
</div>
</template>

<script>
  import { S3Client } from '@aws-sdk/client-s3';
  import { Upload } from '@aws-sdk/lib-storage';
  import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';

  export default {
    name: "UploadForm",
    props: {
      tag: String,
      mime: String,
      prefix: String,
      bucket: String,
      pool: String,
      region: String,
      filename: String,
      filesize: [String, Number],
      original_name: String,
      extension: String,
      check_upload_endpoint: { type: String, default: '' },
    },
    data() {
      return {
        upload_bucket: this.bucket,
        pool_id: this.pool,
        default_region: this.region,
        cur_filename: this.filename,
        cur_original_name: this.original_name,
        cur_filesize: this.filesize || 0,
        cur_extension: this.extension,
        upload_id: null,
        upload_files: {},
        uploading: false,
        complete: false,
        percents: 0,
        preview_content: '',
        show_preview: false,
      }
    },
    methods: {
      selected_file (e) {
        e.stopPropagation();
        e.preventDefault();
        let files = e.target.files ? e.target.files : e.dataTransfer.files;
        this.upload_files[e.target.id] = files[0];
        if (files[0]) {
          this.cur_original_name = files[0].name;
          const extMatch = files[0].name.match(/(.*)(?:\.([^.]+$))/);
          this.cur_extension = extMatch ? extMatch[2] : "";
        }

        // Preview logic for metadata
        this.preview_content = '';
        this.show_preview = false; // Default closed
        if (this.prefix === 'metadata' && files[0]) {
            const reader = new FileReader();
            reader.onload = (e) => {
                this.preview_content = e.target.result;
            };
            reader.readAsText(files[0]);
        }
      },
      async upload (target) {
        const upload_file = this.upload_files[target];

        if( !this.upload_files[target] ){
          alert(this.__('The file is not set.'));
          return;
        }

        let ext = upload_file.name.match(/(.*)(?:\.([^.]+$))/);
        ext = ( ext ) ? ext[2] : "";

        if (this.isDuplicateAttachment(upload_file.name)) {
          alert('同じ名前のファイルはアップロードできません: ' + upload_file.name);
          return;
        }

        if (this.check_upload_endpoint) {
          try {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
            const checkRes = await fetch(this.check_upload_endpoint, {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken,
              },
              body: JSON.stringify({ filesize: upload_file.size }),
            });
            if (!checkRes.ok) {
              const errData = await checkRes.json().catch(() => ({}));
              alert(errData.message || this.__('Upload failed.'));
              return;
            }
          } catch (e) {
            alert(this.__('Upload failed.'));
            return;
          }
        }

        const timestamp = String(new Date().getTime());
        const upload_filename = `upload/${timestamp}/${upload_file.name}`;

        this.cur_filesize = upload_file.size;
        this.cur_filename = upload_filename;
        this.cur_original_name = upload_file.name;
        this.cur_extension = ext;
        const sourceUpdatedAt = upload_file.lastModified
          ? new Date(upload_file.lastModified).toISOString().slice(0, 19).replace('T', ' ')
          : null;

        const params = {
          Bucket: this.upload_bucket,
          Key: upload_filename,
          ContentType: upload_file.type,
          Body: upload_file,
          ACL: "private",
          Tagging: "status=pending"
        };
        this.complete = false;
        this.uploading = true;

        this.$emit('uploading', true);

        const upload = new Upload({
          client: this.s3Client,
          params,
          partSize: 20 * 1024 * 1024,
          queueSize: 20,
        });
        upload.on('httpUploadProgress', evt => this.percents = parseInt( (evt.loaded * 100) / evt.total ) );
        await upload.done();
        this.uploading = false;
        this.$emit('uploading', false);
        this.complete = true;
        this.$emit('complete', {
          tag: this.tag,
          filename: this.cur_filename,
          filesize: this.cur_filesize,
          original_name: this.cur_original_name,
          extension: this.cur_extension,
          source_updated_at: sourceUpdatedAt,
        });

      },
      isDuplicateAttachment(originalName) {
        if (!this.prefix || !(this.prefix === 'attachments' || this.prefix.startsWith('attachments_'))) {
          return false;
        }

        const normalized = this.normalizeFileName(originalName);
        if (!normalized) {
          return false;
        }

        const existingInput = document.getElementById('attachments_existing_names');
        if (existingInput && existingInput.value) {
          try {
            const existing = JSON.parse(existingInput.value)
              .map((name) => this.normalizeFileName(name))
              .filter(Boolean);
            if (existing.includes(normalized)) {
              return true;
            }
          } catch (e) {
            // parse error ignored
          }
        }

        const inputs = document.querySelectorAll('input[id^="attachments_"][id$="_original_name"]');
        for (const input of inputs) {
          if (input.id === `${this.prefix}_original_name`) {
            continue;
          }
          const value = this.normalizeFileName(input.value);
          if (value && value === normalized) {
            return true;
          }
        }

        return false;
      }
      ,
      normalizeFileName(value) {
        if (!value) {
          return '';
        }
        const raw = value.toString().trim().replace(/\\/g, '/');
        const baseName = raw.split('/').pop() || '';
        return baseName.trim().toLowerCase();
      }
    },
    mounted () {
        this.s3Client = new S3Client({
          region: this.default_region,
          credentials: fromCognitoIdentityPool({
            clientConfig: { region: this.default_region },
            identityPoolId: this.pool_id,
          }),
          requestChecksumCalculation: 'WHEN_REQUIRED',
        });
        //this.upload_id = this.$el.dataset.upload_id;
    }
  }
</script>
